import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import {
  User,
  Briefcase,
  Calendar,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  Plus,
  X,
  Loader2,
  Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 1, title: 'Profile', icon: <User className="h-4 w-4" />, description: 'Business information' },
  { id: 2, title: 'Services', icon: <Briefcase className="h-4 w-4" />, description: 'What you offer' },
  { id: 3, title: 'Availability', icon: <Calendar className="h-4 w-4" />, description: 'When you work' },
  { id: 4, title: 'Review', icon: <CheckCircle2 className="h-4 w-4" />, description: 'Confirm & launch' },
];

const CATEGORIES = [
  { value: 'chef', label: 'Chef' },
  { value: 'bartender', label: 'Bartender' },
  { value: 'server', label: 'Server' },
  { value: 'dj', label: 'DJ' },
  { value: 'photographer', label: 'Photographer' },
  { value: 'coordinator', label: 'Event Coordinator' },
  { value: 'setup', label: 'Setup/Breakdown' }
];

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function VendorOnboardingWizard() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);

  // Step 1: Profile
  const [profile, setProfile] = useState({
    business_name: '', description: '', category: 'chef',
    skills: '', hourly_rate: '', service_areas: ''
  });

  // Step 2: Services
  const [servicesList, setServicesList] = useState([
    { name: '', description: '', price: '' }
  ]);

  // Step 3: Availability
  const [availableDays, setAvailableDays] = useState([]);
  const [preferredHours, setPreferredHours] = useState({ start: '09:00', end: '22:00' });
  const [travelRadius, setTravelRadius] = useState('25');

  useEffect(() => {
    loadProgress();
  }, []);

  const loadProgress = async () => {
    try {
      const res = await axios.get(`${API}/vendors/onboarding/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data && res.data.data) {
        const d = res.data.data;
        if (d.step_1) {
          setProfile({
            business_name: d.step_1.business_name || '',
            description: d.step_1.description || '',
            category: d.step_1.category || 'chef',
            skills: (d.step_1.skills || []).join(', '),
            hourly_rate: d.step_1.hourly_rate?.toString() || '',
            service_areas: (d.step_1.service_areas || []).join(', ')
          });
        }
        if (d.step_2?.services?.length > 0) {
          setServicesList(d.step_2.services.map(s => ({
            name: s.name || '', description: s.description || '', price: s.price?.toString() || ''
          })));
        }
        if (d.step_3?.availability) {
          setAvailableDays(d.step_3.availability.available_days || []);
          if (d.step_3.availability.preferred_hours) setPreferredHours(d.step_3.availability.preferred_hours);
          if (d.step_3.availability.travel_radius) setTravelRadius(d.step_3.availability.travel_radius.toString());
        }
        if (res.data.step && res.data.step <= 4) setCurrentStep(res.data.step);
      }
    } catch (e) { /* fresh start */ }
  };

  const saveCurrentStep = async () => {
    setSaving(true);
    let data = {};
    if (currentStep === 1) {
      if (!profile.business_name.trim()) { toast.error('Business name is required'); setSaving(false); return false; }
      data = {
        business_name: profile.business_name.trim(),
        description: profile.description.trim(),
        category: profile.category,
        skills: profile.skills.split(',').map(s => s.trim()).filter(Boolean),
        hourly_rate: parseFloat(profile.hourly_rate) || 0,
        service_areas: profile.service_areas.split(',').map(s => s.trim()).filter(Boolean)
      };
    } else if (currentStep === 2) {
      const valid = servicesList.filter(s => s.name.trim());
      data = {
        services: valid.map(s => ({
          name: s.name.trim(),
          description: s.description.trim(),
          price: parseFloat(s.price) || 0
        }))
      };
    } else if (currentStep === 3) {
      data = {
        availability: {
          available_days: availableDays,
          preferred_hours: preferredHours,
          travel_radius: parseInt(travelRadius) || 25
        }
      };
    }

    try {
      await axios.post(`${API}/vendors/onboarding/save-step`, {
        step: currentStep, data
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSaving(false);
      return true;
    } catch (error) {
      toast.error('Failed to save progress');
      setSaving(false);
      return false;
    }
  };

  const handleNext = async () => {
    const saved = await saveCurrentStep();
    if (saved) setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const handleBack = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const handleComplete = async () => {
    setCompleting(true);
    try {
      // Save step 3 first if on step 4
      if (currentStep === 4) {
        await axios.post(`${API}/vendors/onboarding/save-step`, {
          step: 3,
          data: {
            availability: {
              available_days: availableDays,
              preferred_hours: preferredHours,
              travel_radius: parseInt(travelRadius) || 25
            }
          }
        }, { headers: { Authorization: `Bearer ${token}` } });
      }

      const res = await axios.post(`${API}/vendors/onboarding/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || 'Profile created!');
      navigate('/vendor');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete onboarding');
    } finally {
      setCompleting(false);
    }
  };

  const addService = () => setServicesList(prev => [...prev, { name: '', description: '', price: '' }]);
  const removeService = (idx) => {
    if (servicesList.length <= 1) return;
    setServicesList(prev => prev.filter((_, i) => i !== idx));
  };
  const updateService = (idx, field, value) => {
    setServicesList(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const toggleDay = (day) => {
    setAvailableDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-2">
            Vendor Onboarding
          </h1>
          <p className="text-muted-foreground">Set up your vendor profile step by step</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-1 sm:gap-3 mb-10">
          {STEPS.map((step, idx) => (
            <React.Fragment key={step.id}>
              <button
                onClick={() => step.id <= currentStep && setCurrentStep(step.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                  step.id === currentStep ? 'bg-indigo-500 text-foreground' :
                  step.id < currentStep ? 'bg-indigo-500/20 text-indigo-400 cursor-pointer' :
                  'bg-muted text-muted-foreground'
                }`}
                data-testid={`step-${step.id}`}
              >
                {step.id < currentStep ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  step.icon
                )}
                <span className="text-sm font-medium hidden sm:inline">{step.title}</span>
              </button>
              {idx < STEPS.length - 1 && (
                <ChevronRight className={`h-4 w-4 ${step.id < currentStep ? 'text-indigo-400' : 'text-muted-foreground'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <Card className="bg-card border-border">
          <CardContent className="p-6 sm:p-8">
            {/* Step 1: Profile */}
            {currentStep === 1 && (
              <div className="space-y-5" data-testid="step-1-content">
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-1">Business Profile</h2>
                  <p className="text-sm text-muted-foreground">Tell us about your business</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Business Name *</Label>
                  <Input value={profile.business_name} onChange={(e) => setProfile({...profile, business_name: e.target.value})} placeholder="e.g., Chef Mario's Kitchen" className="bg-muted border-border text-foreground" data-testid="onboard-business-name" />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Category *</Label>
                  <Select value={profile.category} onValueChange={(v) => setProfile({...profile, category: v})}>
                    <SelectTrigger className="bg-muted border-border text-foreground" data-testid="onboard-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Description</Label>
                  <Textarea value={profile.description} onChange={(e) => setProfile({...profile, description: e.target.value})} placeholder="Describe your services and experience..." className="bg-muted border-border text-foreground min-h-[80px]" data-testid="onboard-description" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Hourly Rate ($)</Label>
                    <Input type="number" value={profile.hourly_rate} onChange={(e) => setProfile({...profile, hourly_rate: e.target.value})} placeholder="50" className="bg-muted border-border text-foreground" data-testid="onboard-rate" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Skills (comma-separated)</Label>
                    <Input value={profile.skills} onChange={(e) => setProfile({...profile, skills: e.target.value})} placeholder="Cooking, Plating, Menu Design" className="bg-muted border-border text-foreground" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Service Areas (comma-separated)</Label>
                  <Input value={profile.service_areas} onChange={(e) => setProfile({...profile, service_areas: e.target.value})} placeholder="Los Angeles, Orange County, Ventura" className="bg-muted border-border text-foreground" />
                </div>
              </div>
            )}

            {/* Step 2: Services */}
            {currentStep === 2 && (
              <div className="space-y-5" data-testid="step-2-content">
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-1">Your Services</h2>
                  <p className="text-sm text-muted-foreground">List the services you offer</p>
                </div>
                {servicesList.map((svc, idx) => (
                  <div key={idx} className="bg-muted/50 rounded-lg p-4 space-y-3 border border-border">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground font-medium">Service {idx + 1}</span>
                      {servicesList.length > 1 && (
                        <button onClick={() => removeService(idx)} className="text-muted-foreground hover:text-red-400 p-1">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <Input value={svc.name} onChange={(e) => updateService(idx, 'name', e.target.value)} placeholder="Service name *" className="bg-muted border-border text-foreground h-9 col-span-2" data-testid={`svc-name-${idx}`} />
                      <Input type="number" value={svc.price} onChange={(e) => updateService(idx, 'price', e.target.value)} placeholder="$ Price" className="bg-muted border-border text-foreground h-9" data-testid={`svc-price-${idx}`} />
                    </div>
                    <Input value={svc.description} onChange={(e) => updateService(idx, 'description', e.target.value)} placeholder="Brief description..." className="bg-muted border-border text-foreground h-9" />
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={addService} className="border-border text-muted-foreground" data-testid="add-service-btn">
                  <Plus className="h-3 w-3 mr-1" /> Add Service
                </Button>
              </div>
            )}

            {/* Step 3: Availability */}
            {currentStep === 3 && (
              <div className="space-y-5" data-testid="step-3-content">
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-1">Availability</h2>
                  <p className="text-sm text-muted-foreground">Set your working schedule</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Available Days</Label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS_OF_WEEK.map(day => (
                      <button
                        key={day}
                        onClick={() => toggleDay(day)}
                        className={`px-3 py-2 rounded-lg text-sm transition-all ${
                          availableDays.includes(day)
                            ? 'bg-indigo-500 text-foreground'
                            : 'bg-muted text-muted-foreground border border-border hover:border-indigo-500/50'
                        }`}
                        data-testid={`day-${day.toLowerCase()}`}
                      >
                        {day.slice(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Preferred Start Time</Label>
                    <Input type="time" value={preferredHours.start} onChange={(e) => setPreferredHours({...preferredHours, start: e.target.value})} className="bg-muted border-border text-foreground" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Preferred End Time</Label>
                    <Input type="time" value={preferredHours.end} onChange={(e) => setPreferredHours({...preferredHours, end: e.target.value})} className="bg-muted border-border text-foreground" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Travel Radius (miles)</Label>
                  <Input type="number" value={travelRadius} onChange={(e) => setTravelRadius(e.target.value)} placeholder="25" className="bg-muted border-border text-foreground w-32" data-testid="travel-radius" />
                </div>
              </div>
            )}

            {/* Step 4: Review */}
            {currentStep === 4 && (
              <div className="space-y-5" data-testid="step-4-content">
                <div className="text-center">
                  <Sparkles className="h-10 w-10 text-indigo-400 mx-auto mb-3" />
                  <h2 className="text-xl font-semibold text-foreground mb-1">Review & Launch</h2>
                  <p className="text-sm text-muted-foreground">Make sure everything looks good</p>
                </div>
                
                <div className="space-y-4">
                  {/* Profile Summary */}
                  <div className="bg-muted/50 rounded-lg p-4 border border-border">
                    <h3 className="text-sm font-medium text-indigo-400 mb-3">Profile</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">Business Name</span><span className="text-foreground font-medium">{profile.business_name || '—'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Category</span><span className="text-foreground capitalize">{profile.category}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Hourly Rate</span><span className="text-emerald-400">${profile.hourly_rate || '0'}/hr</span></div>
                      {profile.service_areas && <div className="flex justify-between"><span className="text-muted-foreground">Areas</span><span className="text-foreground">{profile.service_areas}</span></div>}
                    </div>
                  </div>

                  {/* Services Summary */}
                  <div className="bg-muted/50 rounded-lg p-4 border border-border">
                    <h3 className="text-sm font-medium text-indigo-400 mb-3">Services ({servicesList.filter(s => s.name).length})</h3>
                    {servicesList.filter(s => s.name).map((s, i) => (
                      <div key={i} className="flex justify-between text-sm py-1">
                        <span className="text-muted-foreground">{s.name}</span>
                        {s.price && <span className="text-emerald-400">${s.price}</span>}
                      </div>
                    ))}
                    {servicesList.filter(s => s.name).length === 0 && (
                      <p className="text-muted-foreground text-sm">No services added yet</p>
                    )}
                  </div>

                  {/* Availability Summary */}
                  <div className="bg-muted/50 rounded-lg p-4 border border-border">
                    <h3 className="text-sm font-medium text-indigo-400 mb-3">Availability</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex flex-wrap gap-1">
                        {availableDays.length > 0 ? availableDays.map(d => (
                          <Badge key={d} className="bg-indigo-500/20 text-indigo-400 border-indigo-500/30 text-xs">{d.slice(0, 3)}</Badge>
                        )) : <span className="text-muted-foreground">No days selected</span>}
                      </div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Hours</span><span className="text-foreground">{preferredHours.start} - {preferredHours.end}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Travel</span><span className="text-foreground">{travelRadius} miles</span></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
              {currentStep > 1 ? (
                <Button variant="outline" onClick={handleBack} className="border-border text-muted-foreground" data-testid="wizard-back">
                  <ChevronLeft className="h-4 w-4 mr-1" /> Back
                </Button>
              ) : <div />}
              
              {currentStep < 4 ? (
                <Button onClick={handleNext} disabled={saving} className="bg-indigo-500 hover:bg-indigo-600" data-testid="wizard-next">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                  {saving ? 'Saving...' : 'Next'}
                  {!saving && <ChevronRight className="h-4 w-4 ml-1" />}
                </Button>
              ) : (
                <Button onClick={handleComplete} disabled={completing} className="bg-emerald-500 hover:bg-emerald-600" data-testid="wizard-complete">
                  {completing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                  {completing ? 'Creating Profile...' : 'Launch Profile'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
